import { filterSignupSetsBy } from "data"
import { capitalizeEveryWord, serverText } from "functions"
import { useAtomValue } from "jotai"
import { useMemo } from "react"
import { group, iMemberWithLabel, iModal, iSignupSet, isClassSpecLoc } from "typings"
import { useAuth } from "../../../context/authContext"
import { LocationProvider } from "../../../context/locationContext"
import { useModal } from "../../../global/hooks"
import { useLocation } from "../../../global/location"
import { UserDataState } from "../../../global/state/global"
import Popout from "../../1_atom/autoComplete/popOut"
import Empty from "../../1_atom/empty/empty"
import LocationToggle from "../../1_atom/location/locationToggle"
import Characters from "../characters/characters"
import { SignupSet, saveSignupSet } from "../characters/signupSet"
import SignupModal from "../modals/signupModal"
import { SettingSection } from "./settingSection"

const CharactersSettings = () => {
  const auth = useAuth()
  const userData = useAtomValue(UserDataState)

  const SignupSets = () => {
    const [ triggerModal, closeModal ] = useModal()

    const location = useLocation()
    const autoCompleteServerOptions: group<string>[] = useMemo(() => {
      if(isClassSpecLoc(location.location) === false) return []
      const servers = (location.location?.game?.gameServerZones || [])?.map((gameServerZone) => ({
        label: `${gameServerZone.region} - ${gameServerZone.timeZoneLabel}`,
        options: gameServerZone.servers.map(server => ({label: capitalizeEveryWord(serverText(server)), value: server}))
      }))
      return servers
    }, [location.location])

    const signupSets = filterSignupSetsBy(
      userData?.signupSets || {},
      location.map,
    )

    const openSignupModal = (existingSet?: iSignupSet, server?: string) => {
      const modal: iModal = {
        top: "Create new signup set",
        middle: (
          <SignupModal
            location={location.location}
            member={
              existingSet?.set
                ? { ...existingSet?.set, label: existingSet?.label || "New signup set" }
                : undefined
            }
            overrideServer={server}
          />
        ),
        confirmCallback: (input: Partial<iMemberWithLabel>) => {
          saveSignupSet(auth.userData, server, location.map, input, existingSet)
          closeModal()
        }
      }
  
      triggerModal(modal)
    }

    return (
      <div className="flex-col align-start gap1">
        <div className="mb1">
          <Popout
            noOptionsFoundText="No servers found."
            options={autoCompleteServerOptions}
            defaultOption={{
              label: "Create new signup set on server",
              value: "n/a"
            }}
            minWidth={230}
            autoFocus
            isMulti={false}
            onChange={(option) => {
              openSignupModal(undefined, option.value)
              return false
            }}
          />
        </div>
        <div className="flex-row wrap gap1">
          {signupSets?.length > 0 ? (
            signupSets.map((signupSet) => (
              <SignupSet key={signupSet.id} editCallback={openSignupModal} set={signupSet} />
            ))
          ) : (
            <span>You don't have any signup sets yet.</span>
          )}
        </div>
      </div>
    )
  }

  const LocToggle = () => {
    const location = useLocation()
    return (
      <LocationToggle
        locationMap={location.map}
        onChange={(locMap) => {
          location.setMap(locMap)
        }}
      />
    )
  }


  const CharSettings = () => {
    const { location } = useLocation()
    if(isClassSpecLoc(location) === false) return (
      <Empty text="This location does not support characters." className="no-hover" />
    )
    return <Characters showEdit={true} inSettingsPage={true} location={location} />
  }

  return (
    <>
      <LocationProvider>
        <div>
          <h5 className='mb1'>Choose location</h5>
          <LocToggle />
        </div>
        <SettingSection
          title="Characters"
        >
          <CharSettings />
        </SettingSection>
        <SettingSection
          title="Signup sets"
          description="Use signups sets to make your signups even quicker."
        >
          <SignupSets />
        </SettingSection>
      </LocationProvider>
    </>
  )
}

export default CharactersSettings;